/**
 * WordPress dependencies
 */
import { __ } from '@wordpress/i18n';

import { InspectorControls } from '@wordpress/block-editor';

import {
	PanelBody,
	PanelRow,
	RangeControl,
	Button,
	ButtonGroup,
	HorizontalRule,
	TabPanel,
	ColorIndicator,
	ColorPalette,
	Popover,
	SelectControl,
} from '@wordpress/components';

import { useState, useEffect } from '@wordpress/element';

import Icons from '../../utilities/Icons';

const Inspector = ( { attributes, setAttributes } ) => {
	const {
		titleTag,
		descriptionTag,
		gapDBetweenBlocks,
		gapTBetweenBlocks,
		gapMBetweenBlocks,
		blockBorderRadius,
		contentBoxHeight,
		sloganTextAlignment,
		contentBoxPadding,
		deskCols,
		tabCols,
		mobCols,
		titleColor,
		descriptionColor,
		imageObjectFit,
		imageAspectRatio,
	} = attributes;

	const { desktop, tablet, smartphone } = Icons;
	// title color visibility
	const [ isTitleColorVisible, setIsTitleColorVisible ] = useState( false );
	// description color visibility
	const [ isDescriptionColorVisible, setIsDescriptionColorVisible ] =
		useState( false );

	const [ currentButton, setCurrentButton ] = useState( 'desktop' );
	const [ currentGapButton, setCurrentGapButton ] = useState( 'desktop' );

	// Set current button for column number once on mount
	useEffect( () => {
		setCurrentButton( 'desktop' );
	}, [] );

	// Number of Columns device event handler
	const handleColumnsNoClick = ( e ) => {
		// Which device is clicked
		const curButton = e.currentTarget.name;
		// Remove "active" class from all device button classes
		e.currentTarget.parentNode.childNodes.forEach( ( el ) => {
			el.classList.remove( 'active' );
		} );
		// Add "active" to clicked button
		e.currentTarget.classList.add( 'active' );

		setCurrentButton( curButton );
	};

	// Block Gap device event handler
	const handleBlockGapClick = ( e ) => {
		// Which device is clicked
		const curButton = e.currentTarget.name;
		// Remove "active" class from all device button classes
		e.currentTarget.parentNode.childNodes.forEach( ( el ) => {
			el.classList.remove( 'active' );
		} );
		// Add "active" to clicked button
		e.currentTarget.classList.add( 'active' );

		setCurrentGapButton( curButton );
	};

	return (
		<>
			<InspectorControls>
				<PanelBody>
					{ /** BLOCK COLUMN NUMBER **/ }
					<div className="wtrbmr__responsive_items">
						<div className="wtrbmr__label_flex">
							<p className="wtrbmr__label_nomargin">
								{ __(
									'Number of Columns',
									'wtrb-boxslogan-block'
								) }
							</p>
							<div className="wtrbmr__devices">
								<button
									name="desktop"
									className="wtrbmr__device active"
									onClick={ handleColumnsNoClick }
								>
									{ desktop }
								</button>
								<button
									name="tablet"
									className="wtrbmr__device"
									onClick={ handleColumnsNoClick }
								>
									{ tablet }
								</button>
								<button
									name="smartphone"
									className="wtrbmr__device"
									onClick={ handleColumnsNoClick }
								>
									{ smartphone }
								</button>
							</div>
						</div>
						<div className="wtrbmr__items">
							{ currentButton === 'desktop' && (
								<RangeControl
									value={ deskCols }
									onChange={ ( newValue ) => {
										setAttributes( {
											deskCols: newValue,
										} );
									} }
									min={ 1 }
									max={ 4 }
									beforeIcon={ currentButton }
									help={
										__(
											'Desktop Columns: ',
											'wtrb-boxslogan-block'
										) + deskCols
									}
								/>
							) }
							{ currentButton === 'tablet' && (
								<RangeControl
									value={ tabCols }
									onChange={ ( newValue ) => {
										setAttributes( {
											tabCols: newValue,
										} );
									} }
									min={ 1 }
									max={ 2 }
									beforeIcon={ currentButton }
									help={
										__(
											'Tablet Columns: ',
											'wtrb-boxslogan-block'
										) + tabCols
									}
								/>
							) }
							{ currentButton === 'smartphone' && (
								<RangeControl
									value={ mobCols }
									onChange={ ( newValue ) => {
										setAttributes( {
											mobCols: newValue,
										} );
									} }
									min={ 1 }
									max={ 2 }
									beforeIcon={ currentButton }
									help={
										__(
											'Mobile Columns: ',
											'wtrb-boxslogan-block'
										) + mobCols
									}
								/>
							) }
						</div>
					</div>

					{ /** BLOCK GAP **/ }
					<div className="wtrbmr__responsive_items">
						<div className="wtrbmr__label_flex">
							<p className="wtrbmr__label_nomargin">
								{ __(
									'Gap between blocks (px)',
									'wtrb-boxslogan-block'
								) }
							</p>
							<div className="wtrbmr__devices">
								<button
									name="desktop"
									className="wtrbmr__device active"
									onClick={ handleBlockGapClick }
								>
									{ desktop }
								</button>
								<button
									name="tablet"
									className="wtrbmr__device"
									onClick={ handleBlockGapClick }
								>
									{ tablet }
								</button>
								<button
									name="smartphone"
									className="wtrbmr__device"
									onClick={ handleBlockGapClick }
								>
									{ smartphone }
								</button>
							</div>
						</div>
					</div>
					<div className="wtrbmr__items">
						{ currentGapButton === 'desktop' && (
							<RangeControl
								value={ gapDBetweenBlocks || 0 }
								onChange={ ( newValue ) => {
									setAttributes( {
										gapDBetweenBlocks: newValue,
									} );
								} }
								min={ 0 }
								max={ 50 }
								beforeIcon={ currentGapButton }
								help={
									__(
										'Desktop Gap: ',
										'wtrb-boxslogan-block'
									) + gapDBetweenBlocks
								}
							/>
						) }
						{ currentGapButton === 'tablet' && (
							<RangeControl
								value={ gapTBetweenBlocks || 0 }
								onChange={ ( newValue ) => {
									setAttributes( {
										gapTBetweenBlocks: newValue,
									} );
								} }
								min={ 0 }
								max={ 50 }
								beforeIcon={ currentGapButton }
								help={
									__(
										'Tablet Gap: ',
										'wtrb-boxslogan-block'
									) + gapTBetweenBlocks
								}
							/>
						) }
						{ currentGapButton === 'smartphone' && (
							<RangeControl
								value={ gapMBetweenBlocks || 0 }
								onChange={ ( newValue ) => {
									setAttributes( {
										gapMBetweenBlocks: newValue,
									} );
								} }
								min={ 0 }
								max={ 50 }
								beforeIcon={ currentGapButton }
								help={
									__(
										'Mobile Gap: ',
										'wtrb-boxslogan-block'
									) + gapMBetweenBlocks
								}
							/>
						) }
					</div>

					<RangeControl
						label={ __(
							'Block border radius (px)',
							'wtrb-boxslogan-block'
						) }
						separatorType="fullWidth"
						value={ blockBorderRadius || 0 }
						min={ 0 }
						max={ 100 }
						onChange={ ( newValue ) => {
							setAttributes( {
								blockBorderRadius: parseInt( newValue ),
							} );
						} }
					/>
				</PanelBody>
			</InspectorControls>

			<InspectorControls group="styles">
				<div className="wtrb-tabs-panel">
					<PanelBody
						title={ __( 'Content', 'wtrb-boxslogan-block' ) }
						initialOpen={ true }
						className="flex-col"
					>
						<PanelRow>
							{ __( 'Alignment', 'wtrb-boxslogan-block' ) }
						</PanelRow>
						<div className="wtrb__titles_group">
							<ButtonGroup>
								<Button
									onClick={ () => {
										setAttributes( {
											sloganTextAlignment: 'left',
										} );
									} }
									title="Left"
									aria-pressed={ `${
										sloganTextAlignment === 'left'
											? 'true'
											: 'false'
									}` }
									className={ `${
										sloganTextAlignment === 'left'
											? 'is-pressed'
											: ''
									}` }
								>
									<span className="dashicon dashicons dashicons-editor-alignleft"></span>
								</Button>
								<Button
									onClick={ () => {
										setAttributes( {
											sloganTextAlignment: 'center',
										} );
									} }
									title="Center"
									aria-pressed={ `${
										sloganTextAlignment === 'center'
											? 'true'
											: 'false'
									}` }
									className={ `${
										sloganTextAlignment === 'center'
											? 'is-pressed'
											: ''
									}` }
								>
									<span className="dashicon dashicons dashicons-editor-aligncenter"></span>
								</Button>
								<Button
									onClick={ () => {
										setAttributes( {
											sloganTextAlignment: 'right',
										} );
									} }
									title="Right"
									aria-pressed={ `${
										sloganTextAlignment === 'center'
											? 'true'
											: 'false'
									}` }
									className={ `${
										sloganTextAlignment === 'right'
											? 'is-pressed'
											: ''
									}` }
								>
									<span className="dashicon dashicons dashicons-editor-alignright"></span>
								</Button>
							</ButtonGroup>
						</div>

						<PanelRow>
							{ __( 'Title Tag', 'wtrb-boxslogan-block' ) }
						</PanelRow>
						<div className="wtrb__titles_group">
							<ButtonGroup>
								<Button
									onClick={ () => {
										setAttributes( {
											titleTag: 'h1',
										} );
									} }
									title="H1"
									aria-pressed={ `${
										titleTag === 'h1' ? 'true' : 'false'
									}` }
									className={ `${
										titleTag === 'h1' ? 'is-pressed' : ''
									}` }
								>
									H1
								</Button>

								<Button
									onClick={ () => {
										setAttributes( {
											titleTag: 'h2',
										} );
									} }
									title="H2"
									aria-pressed={ `${
										titleTag === 'h2' ? 'true' : 'false'
									}` }
									className={ `${
										titleTag === 'h2' ? 'is-pressed' : ''
									}` }
								>
									H2
								</Button>

								<Button
									onClick={ () => {
										setAttributes( {
											titleTag: 'h3',
										} );
									} }
									title="H3"
									aria-pressed={ `${
										titleTag === 'h3' ? 'true' : 'false'
									}` }
									className={ `${
										titleTag === 'h3' ? 'is-pressed' : ''
									}` }
								>
									H3
								</Button>
								<Button
									onClick={ () => {
										setAttributes( {
											titleTag: 'h4',
										} );
									} }
									title="H4"
									aria-pressed={ `${
										titleTag === 'h4' ? 'true' : 'false'
									}` }
									className={ `${
										titleTag === 'h4' ? 'is-pressed' : ''
									}` }
								>
									H4
								</Button>
								<Button
									onClick={ () => {
										setAttributes( {
											titleTag: 'h5',
										} );
									} }
									title="H5"
									aria-pressed={ `${
										titleTag === 'h5' ? 'true' : 'false'
									}` }
									className={ `${
										titleTag === 'h5' ? 'is-pressed' : ''
									}` }
								>
									H5
								</Button>
								<Button
									onClick={ () => {
										setAttributes( {
											titleTag: 'h6',
										} );
									} }
									title="H6"
									aria-pressed={ `${
										titleTag === 'h6' ? 'true' : 'false'
									}` }
									className={ `${
										titleTag === 'h6' ? 'is-pressed' : ''
									}` }
								>
									H6
								</Button>
								<Button
									onClick={ () => {
										setAttributes( {
											titleTag: 'p',
										} );
									} }
									title="P"
									aria-pressed={ `${
										titleTag === 'p' ? 'true' : 'false'
									}` }
									className={ `${
										titleTag === 'p' ? 'is-pressed' : ''
									}` }
								>
									p
								</Button>
								<Button
									onClick={ () => {
										setAttributes( {
											titleTag: 'div',
										} );
									} }
									title="div"
									aria-pressed={ `${
										titleTag === 'div' ? 'true' : 'false'
									}` }
									className={ `${
										titleTag === 'div' ? 'is-pressed' : ''
									}` }
								>
									div
								</Button>
							</ButtonGroup>
						</div>

						<PanelRow>
							{ __( 'Description Tag', 'wtrb-boxslogan-block' ) }
						</PanelRow>
						<div className="wtrb__titles_group">
							<ButtonGroup>
								<Button
									onClick={ () => {
										setAttributes( {
											descriptionTag: 'h1',
										} );
									} }
									title="H1"
									aria-pressed={ `${
										descriptionTag === 'h1'
											? 'true'
											: 'false'
									}` }
									className={ `${
										descriptionTag === 'h1'
											? 'is-pressed'
											: ''
									}` }
								>
									H1
								</Button>

								<Button
									onClick={ () => {
										setAttributes( {
											descriptionTag: 'h2',
										} );
									} }
									title="H2"
									aria-pressed={ `${
										descriptionTag === 'h2'
											? 'true'
											: 'false'
									}` }
									className={ `${
										descriptionTag === 'h2'
											? 'is-pressed'
											: ''
									}` }
								>
									H2
								</Button>

								<Button
									onClick={ () => {
										setAttributes( {
											descriptionTag: 'h3',
										} );
									} }
									title="H3"
									aria-pressed={ `${
										descriptionTag === 'h3'
											? 'true'
											: 'false'
									}` }
									className={ `${
										descriptionTag === 'h3'
											? 'is-pressed'
											: ''
									}` }
								>
									H3
								</Button>
								<Button
									onClick={ () => {
										setAttributes( {
											descriptionTag: 'h4',
										} );
									} }
									title="H4"
									aria-pressed={ `${
										descriptionTag === 'h4'
											? 'true'
											: 'false'
									}` }
									className={ `${
										descriptionTag === 'h4'
											? 'is-pressed'
											: ''
									}` }
								>
									H4
								</Button>
								<Button
									onClick={ () => {
										setAttributes( {
											descriptionTag: 'h5',
										} );
									} }
									title="H5"
									aria-pressed={ `${
										descriptionTag === 'h5'
											? 'true'
											: 'false'
									}` }
									className={ `${
										descriptionTag === 'h5'
											? 'is-pressed'
											: ''
									}` }
								>
									H5
								</Button>
								<Button
									onClick={ () => {
										setAttributes( {
											descriptionTag: 'h6',
										} );
									} }
									title="H6"
									aria-pressed={ `${
										descriptionTag === 'h6'
											? 'true'
											: 'false'
									}` }
									className={ `${
										descriptionTag === 'h6'
											? 'is-pressed'
											: ''
									}` }
								>
									H6
								</Button>
								<Button
									onClick={ () => {
										setAttributes( {
											descriptionTag: 'p',
										} );
									} }
									title="P"
									aria-pressed={ `${
										descriptionTag === 'p'
											? 'true'
											: 'false'
									}` }
									className={ `${
										descriptionTag === 'p'
											? 'is-pressed'
											: ''
									}` }
								>
									p
								</Button>
								<Button
									onClick={ () => {
										setAttributes( {
											descriptionTag: 'div',
										} );
									} }
									title="div"
									aria-pressed={ `${
										descriptionTag === 'div'
											? 'true'
											: 'false'
									}` }
									className={ `${
										descriptionTag === 'div'
											? 'is-pressed'
											: ''
									}` }
								>
									div
								</Button>
							</ButtonGroup>
						</div>

						<HorizontalRule />
						<RangeControl
							label={ __(
								'Content Height',
								'wtrb-boxslogan-block'
							) }
							separatorType="fullWidth"
							min={ 0 }
							max={ 300 }
							value={ contentBoxHeight || 250 }
							onChange={ ( newValue ) => {
								setAttributes( {
									contentBoxHeight: parseInt( newValue ),
								} );
							} }
						/>

						<RangeControl
							label={ __(
								'Content Padding',
								'wtrb-boxslogan-block'
							) }
							separatorType="fullWidth"
							min={ 10 }
							max={ 50 }
							value={ contentBoxPadding || 10 }
							onChange={ ( newValue ) => {
								setAttributes( {
									contentBoxPadding: parseInt( newValue ),
								} );
							} }
						/>

						<HorizontalRule />
						<div className="wtrb__color_picker">
							<p className="wtrb__color_picker_label">
								{ __( 'Title color', 'wtrb-boxslogan-block' ) }
							</p>
							<button
								onClick={ () => setIsTitleColorVisible( true ) }
								className="wtrb__color_indicator"
							>
								<ColorIndicator
									colorValue={ titleColor || '#000000' }
								/>
								{ isTitleColorVisible && (
									<Popover
										onFocusOutside={ () =>
											setIsTitleColorVisible( false )
										}
									>
										<ColorPalette
											label="Title color"
											value={ titleColor }
											clearable={ true }
											onChange={ ( newColor ) =>
												setAttributes( {
													titleColor: newColor,
												} )
											}
										/>
									</Popover>
								) }
							</button>
						</div>

						<div className="wtrb__color_picker">
							<p className="wtrb__color_picker_label">
								{ __(
									'Description color',
									'wtrb-boxslogan-block'
								) }
							</p>
							<button
								onClick={ () =>
									setIsDescriptionColorVisible( true )
								}
								className="wtrb__color_indicator"
							>
								<ColorIndicator
									colorValue={ descriptionColor || '#444444' }
								/>
								{ isDescriptionColorVisible && (
									<Popover
										onFocusOutside={ () =>
											setIsDescriptionColorVisible(
												false
											)
										}
									>
										<ColorPalette
											label="Description color"
											value={ descriptionColor }
											clearable={ true }
											onChange={ ( newColor ) =>
												setAttributes( {
													descriptionColor: newColor,
												} )
											}
										/>
									</Popover>
								) }
							</button>
						</div>
					</PanelBody>

					<PanelBody
						title={ __( 'Photo', 'wtrb-boxslogan-block' ) }
						initialOpen={ false }
						className="flex-col"
					>
						<SelectControl
							label={ __( 'Object fit', 'wtrb-boxslogan-block' ) }
							value={ imageObjectFit }
							onChange={ ( newValue ) => {
								setAttributes( {
									imageObjectFit: newValue,
								} );
							} }
							options={ [
								{
									label: __(
										'Select an object fit',
										'wtrb-boxslogan-block'
									),
									value: '',
									disabled: true,
								},
								{
									label: __(
										'Inherit',
										'wtrb-boxslogan-block'
									),
									value: 'inherit',
								},
								{
									label: __( 'Fill', 'wtrb-boxslogan-block' ),
									value: 'fill',
								},
								{
									label: __(
										'Contain',
										'wtrb-boxslogan-block'
									),
									value: 'contain',
								},
								{
									label: __(
										'Cover',
										'wtrb-boxslogan-block'
									),
									value: 'cover',
								},
								{
									label: __(
										'Scale down',
										'wtrb-boxslogan-block'
									),
									value: 'scale-down',
								},
							] }
						/>
						<SelectControl
							label={ __(
								'Aspect Ratio',
								'wtrb-boxslogan-block'
							) }
							value={ imageAspectRatio }
							onChange={ ( newValue ) => {
								setAttributes( {
									imageAspectRatio: newValue,
								} );
							} }
							options={ [
								{
									label: __(
										'Select an aspect ratio',
										'wtrb-boxslogan-block'
									),
									value: '',
									disabled: true,
								},
								{
									label: __( '1:1', 'wtrb-boxslogan-block' ),
									value: '1/1',
								},
								{
									label: __( '2:3', 'wtrb-boxslogan-block' ),
									value: '2/3',
								},
								{
									label: __( '3:2', 'wtrb-boxslogan-block' ),
									value: '3/2',
								},
								{
									label: __( '3:4', 'wtrb-boxslogan-block' ),
									value: '3/4',
								},
								{
									label: __( '4:3', 'wtrb-boxslogan-block' ),
									value: '4/3',
								},
								{
									label: __( '9:16', 'wtrb-boxslogan-block' ),
									value: '9/16',
								},
								{
									label: __( '16:9', 'wtrb-boxslogan-block' ),
									value: '16/9',
								},
							] }
						/>
					</PanelBody>
				</div>
			</InspectorControls>
		</>
	);
};

export default Inspector;
